import { Clickable, createComponent } from '@carvana/vdp-components';
import { useContext, useEffect, useState } from 'react';
import AnimateHeight from 'react-animate-height';
import SearchSource from '~/constants/SearchSource';
import AppliedFiltersContext from '~/contexts/AppliedFiltersContext';
import SearchEventsDataContext from '~/contexts/SearchEventsDataContext';
import Close from '~/icons/Close';
import { Tag as TagType } from './constants';
import useGetFilterTags from './useGetFilterTags';
import useRemoveFilterTag from './useRemoveFilterTag';

const Pill = createComponent('button', {
  className: [
    'bg-white hover:bg-transparent border border-solid border-grey-3 focus:border-grey-3 duration-300',
    'flex gap-4 min-h-[34px] bg-white hover:bg-transparent border border-solid border-grey-3 focus:border-grey-3',
    'duration-300 flex gap-4 min-h-[34px] items-center py-4 pl-12 pr-8 rounded-2xl t-body-s text-blue-6',
    'transition-colors whitespace-nowrap l:whitespace-normal'
  ].join(' ')
});

type OwnProps = {
  filterTag: TagType;
};

const Tag = ({ filterTag }: OwnProps) => {
  const [show, setShow] = useState(true);
  const { setSearchSource } = useContext(SearchEventsDataContext);
  const removeTag = useRemoveFilterTag(filterTag);

  return show ? (
    <Pill
      data-analytics-id="remove-applied-filter"
      key={filterTag.tagName}
      onClick={() => {
        setSearchSource(SearchSource.TagRemoved);
        setShow(false);
        removeTag();
      }}
    >
      {filterTag.tagName}
      <Close className="close-icon fill-blue-6 h-[16px] w-[16px]" />
    </Pill>
  ) : null;
};

const AppliedFilters = () => {
  const { setSearchSource } = useContext(SearchEventsDataContext);
  const { appliedFilters, clearAllFilters } = useContext(AppliedFiltersContext);
  const filterTags = useGetFilterTags();
  const [clearAll, setClearAll] = useState(false);

  const handleClearAllTags = () => {
    setSearchSource(SearchSource.TagRemoved);
    setClearAll(true);
    clearAllFilters();
  };

  const showAppliedTags = filterTags && filterTags.length > 0 && !clearAll;

  useEffect(() => {
    setClearAll(false);
  }, [filterTags, appliedFilters]);

  return (
    <AnimateHeight
      duration={500}
      height={showAppliedTags ? 'auto' : 0}
      data-qa="applied-filters"
    >
      <div className="bg-grey-1 flex flex-col pt-24 px-0 l:px-32 l:rounded-t-md">
        <div className="flex flex-row justify-between leading-5 mb-16 px-32 w-full l:px-0">
          <span className="t-header-s text-blue-6">FILTERS</span>
          <Clickable
            className="t-link-s text-grey-5"
            data-analytics-id="clear-all-applied-filters"
            data-qa="clear-all-applied-filters"
            element="span"
            onClick={handleClearAllTags}
          >
            Clear All
          </Clickable>
        </div>
        <div className="flex gap-8 overflow-x-auto pb-24 px-32 w-full l:flex-wrap l:px-0">
          {filterTags?.map(tag => (
            <Tag key={tag.tagName} filterTag={tag} />
          ))}
        </div>
      </div>
    </AnimateHeight>
  );
};

export default AppliedFilters;
